/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { DateRange } from "../utils/date-ranges"

import { getTrainingStatus } from "@/api/admin-api"
import { SettingsService } from "@/services/settings-service"

import {
  useAdminAgentRunsQuery,
  useAdminCostsByUserQuery,
  useAdminDashboardSummaryQuery,
  useAdminDataSourcesQuery,
  useAdminFeatureAdoptionQuery,
  useAdminFeatureUsageStatsQuery,
  useAdminTokenMetricsQuery,
  useAdminTrainingStatusQuery,
  useAdminUploadStatsQuery,
} from "./admin-queries"
import { adminQueryKeys } from "./admin-query-keys"
import { getApproximateDays } from "../utils/date-ranges"

/**
 * Per-section gating for the heavier analytics queries. When a section is
 * collapsed the page passes `false`, so the query stays idle (no request) until
 * the user expands the section. Defaults to enabled to preserve prior behavior
 * for any caller that doesn't gate.
 */
type AdminDataOptions = {
  summaryEnabled?: boolean
  trainingEnabled?: boolean
  dataSourcesEnabled?: boolean
  tokenMetricsEnabled?: boolean
  agentRunsEnabled?: boolean
  adoptionEnabled?: boolean
  costsByUserEnabled?: boolean
}

export function useAdminData(
  dateRange: DateRange | "7" | "30",
  currency: "SEK" | "USD" | "EUR" = "USD",
  options: AdminDataOptions = {}
) {
  const {
    summaryEnabled = true,
    trainingEnabled = true,
    dataSourcesEnabled = true,
    tokenMetricsEnabled = true,
    agentRunsEnabled = true,
    adoptionEnabled = true,
    costsByUserEnabled = true,
  } = options
  const getCurrencySymbol = (curr: string) => {
    switch (curr) {
      case "SEK":
        return "kr"
      case "EUR":
        return "€"
      case "USD":
      default:
        return "$"
    }
  }
  const queryClient = useQueryClient()
  // Convert dateRange to DateRange object if it's a string (backward compatibility)
  const range: DateRange =
    typeof dateRange === "string"
      ? { type: "days", days: parseInt(dateRange) as 7 | 30 }
      : dateRange
  // Calculate approximate days for display purposes (used in chart formatting)
  const numericRange = getApproximateDays(range)
  const [loadStartTime] = useState(() => performance.now())

  // Batched summary query for critical metrics
  // Always enable queries since we default to real data
  const summaryQuery = useAdminDashboardSummaryQuery(range, summaryEnabled, currency)

  // Secondary queries are enabled only while their visible consumer is mounted.
  const trainingQuery = useAdminTrainingStatusQuery(trainingEnabled)
  const dataSourcesQuery = useAdminDataSourcesQuery(dataSourcesEnabled)
  const agentRunsQuery = useAdminAgentRunsQuery(range, agentRunsEnabled)
  const costsByUserQuery = useAdminCostsByUserQuery(range, costsByUserEnabled, currency)
  const featureAdoptionQuery = useAdminFeatureAdoptionQuery(range, adoptionEnabled)
  const uploadStatsQuery = useAdminUploadStatsQuery(range, adoptionEnabled)
  const featureUsageStatsQuery = useAdminFeatureUsageStatsQuery(range, adoptionEnabled)
  const tokenMetricsQuery = useAdminTokenMetricsQuery(range, tokenMetricsEnabled, currency)

  // Aggregate loading and error states - must be defined before useEffect that uses it
  const isLoading =
    summaryQuery.isLoading ||
    trainingQuery.isLoading ||
    // dataSourcesQuery.isLoading || // Isolated loading for data sources
    agentRunsQuery.isLoading ||
    costsByUserQuery.isLoading ||
    featureAdoptionQuery.isLoading ||
    uploadStatsQuery.isLoading ||
    featureUsageStatsQuery.isLoading ||
    tokenMetricsQuery.isLoading

  const isFetchingSummary = summaryQuery.isFetching
  const isFetchingTokenMetrics = tokenMetricsQuery.isFetching
  const isFetchingAgentRuns = agentRunsQuery.isFetching
  const isFetchingCostsByUser = costsByUserQuery.isFetching
  const isFetchingFeatureAdoption = featureAdoptionQuery.isFetching
  const isFetchingUploadStats = uploadStatsQuery.isFetching
  const isFetchingFeatureUsageStats = featureUsageStatsQuery.isFetching
  const isFetchingDataSources = dataSourcesQuery.isFetching
  const isTokenMetricsPending =
    tokenMetricsQuery.isLoading || (tokenMetricsQuery.isFetching && !tokenMetricsQuery.data)

  const isRefetching =
    isFetchingSummary ||
    isFetchingTokenMetrics ||
    isFetchingAgentRuns ||
    isFetchingCostsByUser ||
    isFetchingFeatureAdoption ||
    isFetchingUploadStats ||
    isFetchingFeatureUsageStats ||
    isFetchingDataSources ||
    trainingQuery.isFetching

  const error =
    summaryQuery.error ||
    trainingQuery.error ||
    dataSourcesQuery.error ||
    agentRunsQuery.error ||
    costsByUserQuery.error ||
    featureAdoptionQuery.error ||
    uploadStatsQuery.error ||
    featureUsageStatsQuery.error ||
    tokenMetricsQuery.error

  // Log overall dashboard load time when all queries complete
  useEffect(() => {
    if (!isLoading && (summaryQuery.data || summaryQuery.error)) {
      const totalLoadTime = performance.now() - loadStartTime
      const queryTimes: Array<{ name: string; duration: number; status: string }> = []

      const queries = [
        { name: "dashboardSummary", query: summaryQuery, startTime: loadStartTime },
        { name: "training", query: trainingQuery, startTime: loadStartTime },
        { name: "dataSources", query: dataSourcesQuery, startTime: loadStartTime },
        { name: "agentRuns", query: agentRunsQuery, startTime: loadStartTime },
        { name: "costsByUser", query: costsByUserQuery, startTime: loadStartTime },
        { name: "featureAdoption", query: featureAdoptionQuery, startTime: loadStartTime },
        { name: "uploadStats", query: uploadStatsQuery, startTime: loadStartTime },
        { name: "featureUsageStats", query: featureUsageStatsQuery, startTime: loadStartTime },
        { name: "tokenMetrics", query: tokenMetricsQuery, startTime: loadStartTime },
      ]

      queries.forEach(({ name, query, startTime }) => {
        if (query.dataUpdatedAt) {
          // dataUpdatedAt is in epoch milliseconds, convert to performance.now() scale
          // Since we can't accurately convert between the two time bases, we'll use
          // fetchStatus and the order of completion as indicators instead
          const duration = query.dataUpdatedAt - startTime
          queryTimes.push({
            name,
            duration: duration > 0 && duration < 300000 ? duration : 0, // Cap at 5 minutes, else likely wrong
            status: query.isError ? "error" : "success",
          })
        }
      })

      // Sort by duration descending
      queryTimes.sort((a, b) => b.duration - a.duration)

      // eslint-disable-next-line no-console
      console.group(`[Admin Dashboard] Load Complete - Total: ${totalLoadTime.toFixed(2)}ms`)
      // eslint-disable-next-line no-console
      console.log(
        "Note: Individual query durations may be inaccurate due to timing base differences"
      )
      // eslint-disable-next-line no-console
      console.table(queryTimes)
      // eslint-disable-next-line no-console
      console.groupEnd()
    }
  }, [
    isLoading,
    summaryQuery,
    trainingQuery,
    dataSourcesQuery,
    agentRunsQuery,
    costsByUserQuery,
    featureAdoptionQuery,
    uploadStatsQuery,
    featureUsageStatsQuery,
    tokenMetricsQuery,
    loadStartTime,
  ])

  // Format data from React Query results
  // Check summary query first, then fall back to individual queries
  // This prevents empty state when individual queries complete before summary
  const usageData = summaryQuery.data?.usage
  const searchesData = summaryQuery.data?.searches
  const topUsersData =
    summaryQuery.data?.topUsers || queryClient.getQueryData(adminQueryKeys.topUsers(range))

  // Memoize data transformations to ensure stable object references across renders
  const topUsers = useMemo(() => {
    // Handle case where topUsersData exists but users array is missing or empty
    if (!topUsersData) {
      return null
    }

    if (!Array.isArray(topUsersData.users)) {
      return null
    }

    // Return empty array if users array is empty (so component can show empty state)
    if (topUsersData.users.length === 0) {
      return []
    }

    // Transform users with defensive checks
    return topUsersData.users.map((u) => ({
      name: u.profile?.name || u.email || u.alias || u.userId || "Unknown",
      chats: u.chats,
      tokens: u.tokens.total,
      searches: u.searches,
      company: u.profile?.company ?? null,
      department: u.profile?.department ?? null,
      jobTitle: u.profile?.jobTitle ?? null,
    }))
  }, [topUsersData])

  const agentRunsMonthly = useMemo(() => {
    // Handle case where agentRunsQuery.data exists but months array is missing or empty
    if (!agentRunsQuery.data) {
      return null
    }

    if (!Array.isArray(agentRunsQuery.data.months)) {
      return null
    }

    // Return empty array if months array is empty (so component can show empty state)
    if (agentRunsQuery.data.months.length === 0) {
      return []
    }

    return agentRunsQuery.data.months.map((m) => {
      // Parse the period (could be YYYY-MM-DD for daily or YYYY-MM for monthly)
      // Backend returns YYYY-MM-DD for daily, YYYY-MM for monthly
      const periodStr = m.month
      if (!periodStr) {
        return { month: "Unknown", label: "Unknown", runs: 0 }
      }

      const periodDate =
        numericRange <= 30
          ? new Date(periodStr) // YYYY-MM-DD format for daily
          : new Date(periodStr + "-01") // YYYY-MM format, add day for monthly

      // Use same format as tokenChartData: always "day month" format
      const label = periodDate.toLocaleDateString("sv-SE", { day: "numeric", month: "short" })

      // Sum runs from all agents (should only be research agent now)
      const totalRuns = Array.isArray(m.agents) ? m.agents.reduce((sum, a) => sum + a.runs, 0) : 0

      return {
        month: label, // Keep 'month' for backward compatibility with chart component
        label, // Add 'label' to match tokenChartData structure
        runs: totalRuns,
      }
    })
  }, [agentRunsQuery.data, numericRange])

  const costsByUser = useMemo(() => {
    return costsByUserQuery.data ?? null
  }, [costsByUserQuery.data])

  // Summary only when we have usage data, null when loading or no data
  // Searches are optional - show "0" if not available yet
  const summary = useMemo(() => {
    return usageData
      ? {
          tokens: usageData.summary.tokensTotal.toLocaleString(),
          cost: getCurrencySymbol(currency) + usageData.summary.costUSD.toFixed(2),
          searches: searchesData?.total.toLocaleString() || "0",
          searchesChange: searchesData
            ? `${searchesData.changePct >= 0 ? "+" : ""}${searchesData.changePct.toFixed(0)}%`
            : "+0%",
          users: usageData.summary.usersActive.toString(),
          latency:
            usageData.summary.p95 > 0 && usageData.summary.p95 < 3600000 // Less than 1 hour (reasonable max)
              ? (usageData.summary.p95 / 1000).toFixed(1) + " s"
              : "N/A",
          errors: usageData.summary.errorRate.toFixed(1) + "%",
        }
      : null
  }, [usageData, searchesData, currency])

  const tokenChartData = useMemo(() => {
    return usageData
      ? usageData.points.map((point) => {
          const date = new Date(point.date)
          const label = date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" })

          return {
            label,
            input: point.tokensIn,
            output: point.tokensOut,
            total: point.tokensTotal,
            day: label,
            month: label,
          }
        })
      : null
  }, [usageData])

  const usageTrendData = useMemo(() => {
    return usageData
      ? usageData.points.map((point) => {
          const date = new Date(point.date)
          const label = date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" })

          return {
            label,
            cost: point.costUSD,
            tokensTotal: point.tokensTotal,
            requests: point.requests,
          }
        })
      : null
  }, [usageData])

  const searchChartData = useMemo(() => {
    return searchesData
      ? searchesData.points.map((point) => {
          const date = new Date(point.date)
          const label = date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" })

          return {
            label,
            month: label,
            searches: point.queries,
          }
        })
      : null
  }, [searchesData])

  const dataSources = useMemo(() => {
    return dataSourcesQuery.data
      ? dataSourcesQuery.data.sources.map((s) => ({
          name: s.name,
          type: s.type,
          status: s.status,
          items: s.items,
          lastSync: s.lastSync
            ? new Date(s.lastSync).toLocaleString("sv-SE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—",
        }))
      : null
  }, [dataSourcesQuery.data])

  const featureAdoption = useMemo(() => {
    return featureAdoptionQuery.data
      ? {
          sessionTypes: Object.entries(featureAdoptionQuery.data.session_types).map(
            ([type, count]) => ({
              type,
              count,
              percentage:
                featureAdoptionQuery.data.total_sessions > 0
                  ? (count / featureAdoptionQuery.data.total_sessions) * 100
                  : 0,
            })
          ),
          projectAdoptionRate:
            featureAdoptionQuery.data.total_sessions > 0
              ? (featureAdoptionQuery.data.project_sessions /
                  featureAdoptionQuery.data.total_sessions) *
                100
              : 0,
          uploadAdoptionRate:
            featureAdoptionQuery.data.total_sessions > 0
              ? (featureAdoptionQuery.data.upload_sessions /
                  featureAdoptionQuery.data.total_sessions) *
                100
              : 0,
          totalSessions: featureAdoptionQuery.data.total_sessions,
        }
      : null
  }, [featureAdoptionQuery.data])

  const uploadStats = useMemo(() => {
    return uploadStatsQuery.data
      ? {
          totalFiles: uploadStatsQuery.data.total_files,
          totalSizeMB: uploadStatsQuery.data.total_size_mb,
          sessionsWithUploads: uploadStatsQuery.data.sessions_with_uploads,
        }
      : null
  }, [uploadStatsQuery.data])

  const featureUsageStats = useMemo(() => {
    if (!featureUsageStatsQuery.data) return null
    // Defensive fallbacks: the admin metrics cache can hold snapshots from a
    // previous backend version. When the ExplorerAgent → Scholar rename
    // landed, old cached rows were keyed on `explorer_agent`, and other
    // feature-shape fields may be absent on older rows. We route through a
    // loose view that treats every nested property as optional so the card
    // components can assume shape without crashing on `.count` reads.
    const raw = featureUsageStatsQuery.data as unknown as Record<string, unknown>
    const pick = <T>(key: string, fallback: T): T => {
      const value = raw[key]
      return value == null ? fallback : (value as T)
    }
    return {
      totalMessages: pick<number>("total_messages", 0),
      filePicker: pick("file_picker", {
        count: 0,
        percentage: 0,
        avg_files: 0,
        total_size_mb: 0,
      }),
      highReasoning: pick("high_reasoning", {
        count: 0,
        percentage: 0,
        effort_levels: {} as Record<string, number>,
      }),
      webSearch: pick("web_search", { count: 0, percentage: 0 }),
      trustedSources: pick("trusted_sources", {
        count: 0,
        percentage: 0,
        only_mode_count: 0,
      }),
      scholar: pick("scholar", pick("explorer_agent", { count: 0, with_research_count: 0 })),
      promptLibrary: pick("prompt_library", {
        count: 0,
        top_prompts: [] as Array<{ id: string; title: string; count: number }>,
      }),
      flow: pick("flow", {
        count: 0,
        top_workflows: [] as Array<{ id: string; count: number }>,
      }),
      activatedLibraries: pick("activated_libraries", {
        count: 0,
        top_libraries: [] as Array<{ id: string; count: number }>,
      }),
      toolApprovals: pick("tool_approvals", {
        count: 0,
        approved: 0,
        denied: 0,
        expired: 0,
        by_tool: [] as Array<{ tool: string; count: number }>,
      }),
    }
  }, [featureUsageStatsQuery.data])

  const tokenMetrics = useMemo(() => {
    if (!tokenMetricsQuery.data || isTokenMetricsPending) {
      return null
    }

    // Hosted-agent run usage (Fas 6). Optional on the wire (older cached
    // payloads / empty aggregation) — normalize "absent or empty" to null.
    const hostedAgentsRaw = tokenMetricsQuery.data.hosted_agents
    const hostedAgents =
      hostedAgentsRaw && typeof hostedAgentsRaw.run_count === "number"
        ? {
            run_count: hostedAgentsRaw.run_count,
            total_tokens: hostedAgentsRaw.total_tokens ?? 0,
            cost_sek: hostedAgentsRaw.cost_sek ?? 0,
          }
        : null

    return {
      totalInputTokens: tokenMetricsQuery.data.total_input_tokens,
      totalOutputTokens: tokenMetricsQuery.data.total_output_tokens,
      totalReasoningTokens: tokenMetricsQuery.data.total_reasoning_tokens,
      totalCachedTokens: tokenMetricsQuery.data.total_cached_tokens,
      totalTokens: tokenMetricsQuery.data.total_tokens,
      totalRequests: tokenMetricsQuery.data.total_requests,
      byModel: tokenMetricsQuery.data.by_model,
      cacheEfficiency: tokenMetricsQuery.data.cache_efficiency,
      totalCostUsd: tokenMetricsQuery.data.total_cost_usd,
      hostedAgents,
    }
  }, [isTokenMetricsPending, tokenMetricsQuery.data])

  // Force refresh training data (bypasses backend cache)
  const refetchTraining = async () => {
    try {
      const freshData = await getTrainingStatus(true)
      queryClient.setQueryData(adminQueryKeys.trainingStatus(), freshData)
      return { status: "success" }
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingStatus() })
      throw error
    }
  }

  // Refetch function - invalidate all admin queries
  const refetch = async () => {
    try {
      // Call the refresh endpoint explicitly to trigger cache update
      // We need to access the API client which might be tricky here if not exported
      // For now, invalidating queries will trigger re-fetching, but if we want to
      // enforce the backend throttle message, we should probably call an explicit refresh endpoint
      // or rely on the fact that invalidating queries will cause GET requests that might return cached data
      // If we want the "skipped" message, we need to call the POST /api/admin/refresh endpoint

      const token = SettingsService.getInstance().token
      const response = await fetch("/api/admin/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dateRange: range,
          currency,
        }),
      })

      const result = await response.json()

      if (result.status === "skipped") {
        return { status: "skipped", message: result.message }
      }

      // If successful, invalidate queries to fetch fresh data
      await queryClient.invalidateQueries({ queryKey: adminQueryKeys.root })
      return { status: "success" }
    } catch (error) {
      // Error handled by React Query mutation
      // Fallback to just invalidating
      await queryClient.invalidateQueries({ queryKey: adminQueryKeys.root })
      throw error
    }
  }

  return {
    topUsers,
    summary,
    summaryComputedAt: summaryQuery.data?.computedAt,
    tokenChartData,
    usageTrendData,
    searchChartData,
    dataSources,
    agentRunsMonthly,
    costsByUser,
    featureAdoption,
    uploadStats,
    featureUsageStats,
    tokenMetrics,
    training: trainingQuery.data || null,
    isLoading,
    isRefetching,
    isFetchingSummary,
    // Individual query loading states
    isLoadingTokenMetrics: isTokenMetricsPending,
    isLoadingCostSummary: summaryQuery.isLoading || isFetchingSummary,
    isLoadingTokenMetricsFetching: isFetchingTokenMetrics, // Show loading when fetching (currency change)
    isLoadingTopUsers:
      summaryQuery.isLoading || (isFetchingSummary && !summaryQuery.data?.topUsers),
    isLoadingTraining: trainingQuery.isLoading,
    isFetchingTraining: trainingQuery.isFetching,
    refetchTraining,
    isLoadingDataSources: dataSourcesQuery.isLoading,
    isLoadingAgentRuns:
      agentRunsQuery.isLoading || (isFetchingAgentRuns && !agentRunsQuery.data?.months),
    isLoadingCostsByUser: costsByUserQuery.isLoading,
    isLoadingFeatureAdoption: featureAdoptionQuery.isLoading,
    isLoadingUploadStats: uploadStatsQuery.isLoading,
    isLoadingFeatureUsageStats: featureUsageStatsQuery.isLoading,
    isLoadingSearches: summaryQuery.isLoading,
    isLoadingUsage: summaryQuery.isLoading,
    error,
    refetch,
  }
}
